import { ISyndicateData } from './Syndicate';
import { appRoutes } from '../../../path';
import { assertNotNil } from '../../../utils/types';
import { Api } from './authApi';

// save redirect url in sessionStorage
// for example, user enters /dashboard without login, we save /dashboard in sessionStorage
const REDIRECT_URL_KEY = 'redirectUrl';

// exceptions on redirect urls
const PATHNAME_NO_NEED_TO_REDIRECT = [
  appRoutes.ssoLogin,
  appRoutes.login,
  appRoutes.errorPage,
  appRoutes.embedLogin,
];

const SAML_LOGIN_ENDPOINT = '/auth/saml/login';

class Cognito {
  private syndicateData: ISyndicateData | undefined;
  private authApi = new Api();
  public setSyndicateData = (syndicateData: ISyndicateData) => {
    this.syndicateData = syndicateData;
  };
  public fetchMfaSecretCode = () => {
    return this.authApi.auth.routesAuthMfaConfigureConfigure();
  };

  public confirmMfa = (verifyToken: string) => {
    return this.authApi.auth.routesAuthMfaConfirmConfirm({
      code: verifyToken,
    });
  };

  public verifyMfa = (email: string, verifyToken: string) => {
    const syndicateData = this.syndicateData;
    assertNotNil(syndicateData);
    const encodedData = window.AmazonCognitoAdvancedSecurityData.getData(
      email,
      syndicateData.userPoolId,
      syndicateData.clientId,
    );
    return this.authApi.auth.routesAuthMfaVerifyVerify(
      {
        username: email,
        code: verifyToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Encoded-Data': encodedData,
        },
      },
    );
  };
  public changePassword = (
    username: string,
    oldPassword: string,
    newPassword: string,
  ) => {
    const syndicateData = this.syndicateData;
    assertNotNil(syndicateData);
    const encodedData = window.AmazonCognitoAdvancedSecurityData.getData(
      username,
      syndicateData.userPoolId,
      syndicateData.clientId,
    );
    assertNotNil(encodedData);
    return this.authApi.auth.routesAuthPasswordChangePassword(
      {
        username,
        oldPassword,
        newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Encoded-Data': encodedData,
        },
      },
    );
  };

  public usernameLogin = async (username: string, password: string) => {
    const syndicateData = this.syndicateData;
    assertNotNil(syndicateData);
    const encodedData = window.AmazonCognitoAdvancedSecurityData.getData(
      username,
      syndicateData.userPoolId,
      syndicateData.clientId,
    );
    return this.authApi.auth.routesAuthLoginLogin(
      {
        username,
        password,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Encoded-Data': encodedData,
        },
      },
    );
  };
  public samlLogin = () => {
    window.location.href = SAML_LOGIN_ENDPOINT;
  };
  public logout = async () => {
    return this.authApi.auth.routesAuthTokenLogoutLogout();
  };

  public refreshToken = async () => {
    return this.authApi.auth.routesAuthTokenRefreshRefreshIdToken();
  };

  // Saved Redirect URL is used in 2 cases:
  // 1. When user enters without login (ApolloClient error handler), we save the current URL and redirect to /login
  // 2. when user logs out (<UserDropdown />), we save the current URL and redirect to /login
  // we will only retrieve and clean the saved redirect URL in login mutation success handler.
  public setRedirectUrl = (pathname: string) => {
    if (!PATHNAME_NO_NEED_TO_REDIRECT.includes(pathname)) {
      sessionStorage.setItem(
        REDIRECT_URL_KEY,
        pathname + window.location.search,
      );
    }
  };
  public getRedirectUrl = () => {
    const redirectUrl = sessionStorage.getItem(REDIRECT_URL_KEY);
    if (
      !redirectUrl ||
      redirectUrl.length === 0 ||
      PATHNAME_NO_NEED_TO_REDIRECT.includes(redirectUrl)
    ) {
      return '';
    } else {
      return redirectUrl;
    }
  };

  public clearRedirectUrl = () => {
    sessionStorage.removeItem(REDIRECT_URL_KEY);
  };

  public getLoginPageUrl = () => {
    assertNotNil(this.syndicateData);

    const { enableSso } = this.syndicateData;

    if (enableSso) {
      return appRoutes.ssoLogin;
    } else {
      return appRoutes.login;
    }
  };

  public forgotPassword = (email: string) => {
    const syndicateData = this.syndicateData;
    assertNotNil(syndicateData);
    const encodedData = window.AmazonCognitoAdvancedSecurityData.getData(
      email,
      syndicateData.userPoolId,
      syndicateData.clientId,
    );
    return this.authApi.auth.routesAuthRecoveryForgotPasswordForgotPassword(
      {
        username: email,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Encoded-Data': encodedData,
        },
      },
    );
  };

  public confirmForgotPassword = (
    email: string,
    code: string,
    newPassword: string,
  ) => {
    const syndicateData = this.syndicateData;
    assertNotNil(syndicateData);
    const encodedData = window.AmazonCognitoAdvancedSecurityData.getData(
      email,
      syndicateData.userPoolId,
      syndicateData.clientId,
    );
    assertNotNil(encodedData);
    return this.authApi.auth.routesAuthRecoveryConfirmPasswordConfirmPassword(
      {
        username: email,
        confirmationCode: code,
        newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Encoded-Data': encodedData,
        },
      },
    );
  };
}

const CognitoObject = new Cognito();
export default CognitoObject;
